import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDown,
  faArrowUp,
  faTimes,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import NoImage from 'Assets/Images/Logos/no-image.png'
import CatalogImageInput from './CatalogImageInput'
import {
  assignProductsToSection,
  createCatalogSection,
  getCatalogProductImageUrl,
  getSectionProducts,
  moveItem,
  moveProductInSection,
  unassignSectionProducts,
} from '../utils/quickListCatalog.utils'

function OrderButtons({ onUp, onDown, disableUp, disableDown, testId }) {
  return (
    <>
      <button
        type="button"
        className="catalog-icon-button"
        title={t('QUICK_LIST_CATALOG.SECTIONS.MOVE_UP')}
        aria-label={t('QUICK_LIST_CATALOG.SECTIONS.MOVE_UP')}
        disabled={disableUp}
        onClick={onUp}
        data-testid={`btn-move-up-${testId}`}>
        <FontAwesomeIcon icon={faArrowUp} />
      </button>
      <button
        type="button"
        className="catalog-icon-button"
        title={t('QUICK_LIST_CATALOG.SECTIONS.MOVE_DOWN')}
        aria-label={t('QUICK_LIST_CATALOG.SECTIONS.MOVE_DOWN')}
        disabled={disableDown}
        onClick={onDown}
        data-testid={`btn-move-down-${testId}`}>
        <FontAwesomeIcon icon={faArrowDown} />
      </button>
    </>
  )
}

// latestValuesRef viene de QuickListCatalog: sigue actualizado aunque este paso se desmonte
// mientras termina una subida (p. ej. si el usuario cambia de paso).
function CatalogSectionsStep({
  formik,
  entityId,
  sectionsError,
  latestValuesRef,
}) {
  const { sections, products } = formik.values
  const [selectedSectionId, setSelectedSectionId] = useState(
    sections[0]?.catalogSectionId || null
  )
  const [productsToAssign, setProductsToAssign] = useState([])

  const selectedIndex = sections.findIndex(
    section => section.catalogSectionId === selectedSectionId
  )
  const selectedSection = sections[selectedIndex]
  const sectionProducts = selectedSection
    ? getSectionProducts(products, selectedSection.catalogSectionId)
    : []
  const unassignedProducts = products.filter(
    product => !product.catalogSectionId
  )

  useEffect(() => {
    setProductsToAssign([])
  }, [selectedSectionId])

  useEffect(() => {
    if (!selectedSection && sections.length > 0) {
      setSelectedSectionId(sections[0].catalogSectionId)
    }
  }, [sections, selectedSection])

  const setSections = nextSections =>
    formik.setFieldValue('sections', nextSections)
  const setProducts = nextProducts =>
    formik.setFieldValue('products', nextProducts)

  // Actualiza una sección por su id sobre los valores actuales del formulario: las subidas de
  // imágenes terminan después y no deben pisar renombres ni revivir secciones borradas.
  const updateSection = (sectionId, getChanges) => {
    const latestSections = latestValuesRef.current.sections
    if (!latestSections.some(section => section.catalogSectionId === sectionId))
      return

    setSections(
      latestSections.map(section =>
        section.catalogSectionId === sectionId
          ? { ...section, ...getChanges(section) }
          : section
      )
    )
  }

  const updateSelectedSection = changes =>
    updateSection(selectedSection.catalogSectionId, () => changes)

  const handleAddSection = () => {
    const section = createCatalogSection(
      `${t('QUICK_LIST_CATALOG.SECTIONS.DEFAULT_NAME')} ${sections.length + 1}`
    )
    setSections([...sections, section])
    setSelectedSectionId(section.catalogSectionId)
  }

  const handleDeleteSection = sectionId => {
    setSections(
      sections.filter(section => section.catalogSectionId !== sectionId)
    )
    setProducts(unassignSectionProducts(products, sectionId))
    if (sectionId === selectedSectionId) setSelectedSectionId(null)
  }

  const toggleProductToAssign = productId =>
    setProductsToAssign(current =>
      current.includes(productId)
        ? current.filter(id => id !== productId)
        : [...current, productId]
    )

  const handleAssignProducts = () => {
    setProducts(
      assignProductsToSection(
        products,
        productsToAssign,
        selectedSection.catalogSectionId
      )
    )
    setProductsToAssign([])
  }

  const handleRemoveProduct = productId =>
    setProducts(
      products.map(product =>
        product.productId === productId
          ? { ...product, catalogSectionId: null }
          : product
      )
    )

  return (
    <div
      className="catalog-step catalog-sections"
      data-testid="container-step-sections">
      <aside className="catalog-sections-list">
        <div className="quick-list-section-header">
          <label className="quick-list-section-title">
            {t('QUICK_LIST_CATALOG.SECTIONS.TITLE')}
            <span className="required">*</span>
          </label>
          <button
            type="button"
            className="btn-add"
            onClick={handleAddSection}
            data-testid="btn-add-section">
            {t('QUICK_LIST_CATALOG.SECTIONS.ADD')}
          </button>
        </div>

        {sections.length === 0 ? (
          <p
            className="quick-list-section-empty"
            data-testid="text-empty-sections-catalog">
            {t('QUICK_LIST_CATALOG.SECTIONS.EMPTY')}
          </p>
        ) : (
          <ol
            className="catalog-sections-items"
            data-testid="list-sections-catalog">
            {sections.map((section, index) => {
              const count = getSectionProducts(
                products,
                section.catalogSectionId
              ).length
              const isSelected = section.catalogSectionId === selectedSectionId
              return (
                <li
                  key={section.catalogSectionId}
                  className={isSelected ? 'is-selected' : ''}
                  data-testid={`item-section-${section.catalogSectionId}`}>
                  <button
                    type="button"
                    className="catalog-sections-item-name"
                    onClick={() =>
                      setSelectedSectionId(section.catalogSectionId)
                    }
                    data-testid={`btn-select-section-${section.catalogSectionId}`}>
                    <span>{section.name || '—'}</span>
                    <small>
                      {t('QUICK_LIST_CATALOG.SECTIONS.PRODUCTS_COUNT', {
                        count,
                      })}
                    </small>
                  </button>
                  <div className="catalog-sections-item-actions">
                    <OrderButtons
                      testId={`section-${section.catalogSectionId}`}
                      disableUp={index === 0}
                      disableDown={index === sections.length - 1}
                      onUp={() => setSections(moveItem(sections, index, -1))}
                      onDown={() => setSections(moveItem(sections, index, 1))}
                    />
                    <button
                      type="button"
                      className="catalog-icon-button catalog-icon-button--danger"
                      title={t('QUICK_LIST_CATALOG.SECTIONS.DELETE')}
                      aria-label={t('QUICK_LIST_CATALOG.SECTIONS.DELETE')}
                      onClick={() =>
                        handleDeleteSection(section.catalogSectionId)
                      }
                      data-testid={`btn-delete-section-${section.catalogSectionId}`}>
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </li>
              )
            })}
          </ol>
        )}

        {sectionsError && (
          <div className="input-feedback">
            <strong data-testid="text-error-sections-catalog">
              {sectionsError}
            </strong>
          </div>
        )}
      </aside>

      <section className="catalog-section-detail">
        {!selectedSection ? (
          <p
            className="quick-list-section-empty"
            data-testid="text-select-section-catalog">
            {t('QUICK_LIST_CATALOG.SECTIONS.SELECT_SECTION')}
          </p>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="sectionName">
                {t('QUICK_LIST_CATALOG.SECTIONS.NAME')}
                <span className="required">*</span>
              </label>
              <input
                id="sectionName"
                type="text"
                maxLength={120}
                className={
                  selectedSection.name.trim()
                    ? 'form-control'
                    : 'form-control error'
                }
                value={selectedSection.name}
                onChange={event =>
                  updateSelectedSection({ name: event.target.value })
                }
                data-testid="input-name-section"
              />
            </div>

            <div className="catalog-section-media">
              <CatalogImageInput
                id="sectionBanner"
                label={t('QUICK_LIST_CATALOG.SECTIONS.BANNER')}
                value={selectedSection.bannerImageUrl}
                entityId={entityId}
                variant="banner"
                onChange={url =>
                  updateSection(selectedSection.catalogSectionId, () => ({
                    bannerImageUrl: url,
                  }))
                }
                testId="section-banner"
              />
              <CatalogImageInput
                id="sectionIcon"
                label={t('QUICK_LIST_CATALOG.SECTIONS.ICON')}
                value={selectedSection.iconUrl}
                entityId={entityId}
                variant="icon"
                onChange={url =>
                  updateSection(selectedSection.catalogSectionId, () => ({
                    iconUrl: url,
                  }))
                }
                testId="section-icon"
              />
            </div>

            <div className="form-group">
              <label>{t('QUICK_LIST_CATALOG.SECTIONS.SECONDARY_IMAGES')}</label>
              <div className="catalog-gallery">
                {selectedSection.secondaryImages.map((url, index) => (
                  <figure
                    key={`${url}-${index}`}
                    className="catalog-gallery-item">
                    <img src={url} alt="" />
                    <button
                      type="button"
                      className="catalog-gallery-remove"
                      aria-label={t('QUICK_LIST_CATALOG.IMAGE.REMOVE')}
                      onClick={() =>
                        updateSection(
                          selectedSection.catalogSectionId,
                          section => ({
                            secondaryImages: section.secondaryImages.filter(
                              (_, imageIndex) => imageIndex !== index
                            ),
                          })
                        )
                      }
                      data-testid={`btn-remove-secondary-${index}`}>
                      &times;
                    </button>
                  </figure>
                ))}
                <CatalogImageInput
                  id="sectionSecondaryImage"
                  value=""
                  entityId={entityId}
                  variant="icon"
                  hideRemove
                  onChange={url =>
                    url &&
                    updateSection(
                      selectedSection.catalogSectionId,
                      section => ({
                        secondaryImages: [...section.secondaryImages, url],
                      })
                    )
                  }
                  testId="section-secondary"
                />
              </div>
            </div>

            <div className="quick-list-section">
              <div className="quick-list-section-header">
                <label className="quick-list-section-title">
                  {t('QUICK_LIST_CATALOG.SECTIONS.PRODUCTS')}
                </label>
              </div>
              {sectionProducts.length === 0 ? (
                <p
                  className="quick-list-section-empty"
                  data-testid="text-empty-products-section">
                  {t('QUICK_LIST_CATALOG.SECTIONS.NO_PRODUCTS')}
                </p>
              ) : (
                <div className="quick-list-section-table-wrapper">
                  <table className="quick-list-section-table catalog-section-products">
                    <colgroup>
                      <col className="catalog-col-thumb" />
                      <col />
                      <col className="catalog-col-actions" />
                    </colgroup>
                    <tbody>
                      {sectionProducts.map((product, index) => (
                        <tr key={product.productId}>
                          <td>
                            <img
                              src={
                                getCatalogProductImageUrl(product) || NoImage
                              }
                              alt={product.productName || ''}
                              width={40}
                              height={40}
                              className="quick-list-section-thumb"
                            />
                          </td>
                          <td>
                            <div className="catalog-product-cell">
                              <span className="catalog-product-name">
                                {product.productName}
                              </span>
                              <small>{product.sku}</small>
                            </div>
                          </td>
                          <td>
                            <div className="catalog-row-actions">
                              <OrderButtons
                                testId={`section-product-${product.productId}`}
                                disableUp={index === 0}
                                disableDown={
                                  index === sectionProducts.length - 1
                                }
                                onUp={() =>
                                  setProducts(
                                    moveProductInSection(
                                      products,
                                      product.productId,
                                      -1
                                    )
                                  )
                                }
                                onDown={() =>
                                  setProducts(
                                    moveProductInSection(
                                      products,
                                      product.productId,
                                      1
                                    )
                                  )
                                }
                              />
                              <button
                                type="button"
                                className="catalog-icon-button catalog-icon-button--danger"
                                title={t(
                                  'QUICK_LIST_CATALOG.SECTIONS.REMOVE_PRODUCT'
                                )}
                                aria-label={t(
                                  'QUICK_LIST_CATALOG.SECTIONS.REMOVE_PRODUCT'
                                )}
                                onClick={() =>
                                  handleRemoveProduct(product.productId)
                                }
                                data-testid={`btn-unassign-${product.productId}`}>
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="quick-list-section">
              <div className="quick-list-section-header">
                <label className="quick-list-section-title">
                  {t('QUICK_LIST_CATALOG.SECTIONS.AVAILABLE_PRODUCTS')}
                </label>
                <button
                  type="button"
                  className="btn-add"
                  disabled={productsToAssign.length === 0}
                  onClick={handleAssignProducts}
                  data-testid="btn-assign-products">
                  {t('QUICK_LIST_CATALOG.SECTIONS.ASSIGN', {
                    count: productsToAssign.length,
                  })}
                </button>
              </div>
              {unassignedProducts.length === 0 ? (
                <p
                  className="quick-list-section-empty"
                  data-testid="text-empty-available-products-section">
                  {t('QUICK_LIST_CATALOG.SECTIONS.NO_AVAILABLE')}
                </p>
              ) : (
                <ul
                  className="catalog-available-products"
                  data-testid="list-available-products-section">
                  {unassignedProducts.map(product => (
                    <li
                      key={product.productId}
                      data-testid={`item-available-product-${product.productId}`}>
                      <label>
                        <input
                          type="checkbox"
                          checked={productsToAssign.includes(product.productId)}
                          onChange={() =>
                            toggleProductToAssign(product.productId)
                          }
                          data-testid={`input-assign-product-${product.productId}`}
                        />
                        <span>{product.productName}</span>
                        <small>{product.sku}</small>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  )
}

export default CatalogSectionsStep
